import React from 'react';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { Button, Flex, TagBadge, Text, useToast } from '@chatsdet/ui';
import {
  useCreateTicket,
  useTicketingProviderStatus,
  useAutoFileFailure,
  useTicketLink,
  type TicketingProviderSummary,
} from '../../../hooks/useTicketingProviders';
import { describeMissingTaxonomy } from '../../../features/tickets/taxonomy';
import { toastErrorOptionsFor } from '../../../lib/toast-error-options';
import { getTicketingProviderColor } from '../../../utils/ticketingProviders';
import { useTicketingProviderOptions } from './ticketingProviderOptions';

type IssueTicketingProvidersProps = {
  issueId: string;
  providers: TicketingProviderSummary[];
};

type IssueTicketingProviderRowProps = {
  issueId: string;
  provider: TicketingProviderSummary;
};

function IssueTicketingProviderRow({ issueId, provider }: IssueTicketingProviderRowProps) {
  const { toast } = useToast();
  const [isConfiguring, setIsConfiguring] = React.useState(false);
  const linkQuery = useTicketLink(provider.id, issueId);
  const statusQuery = useTicketingProviderStatus(provider.id, provider.capabilities.createTicket);
  const createTicketMutation = useCreateTicket(provider.id);
  const link = linkQuery.data?.data ?? null;
  const connected = statusQuery.data?.data?.connected === true;
  const canCreate =
    provider.capabilities.createTicket && connected && !link && !linkQuery.isLoading;
  const autoFileReason = useAutoFileFailure(
    provider.id,
    issueId,
    canCreate && provider.capabilities.filingSettings
  ).data?.data?.reason;
  const autoFileWarning = autoFileReason ? (
    <Text size="sm" className="text-warning w-full">
      Auto-file failed: {autoFileReason}
    </Text>
  ) : null;
  const closeOptions = React.useCallback(() => setIsConfiguring(false), []);
  const options = useTicketingProviderOptions(
    provider,
    isConfiguring,
    issueId,
    canCreate,
    closeOptions
  );

  React.useEffect(() => {
    setIsConfiguring(false);
  }, [issueId, provider.id]);

  const handleCreateTicket = React.useCallback(() => {
    createTicketMutation.mutate(
      {
        issueId,
        providerOptions: options.providerOptions,
      },
      {
        onSuccess: (data) => {
          setIsConfiguring(false);
          const { identifier, linkedExisting, dropped } = data.ticket;
          if (linkedExisting) {
            toast.info(`Linked existing ticket ${identifier}`);
          } else {
            toast.success(`Created ${identifier}`);
          }
          if (dropped) {
            toast.warning(
              `${identifier} was filed without ${describeMissingTaxonomy(dropped.type, dropped.labels)}: not found in the tracker`
            );
          }
        },
        onError: (error) => {
          const message =
            error instanceof Error
              ? error.message
              : `Failed to create ${provider.displayName} ticket`;
          toast.error(message, toastErrorOptionsFor(error));
        },
      }
    );
  }, [createTicketMutation, issueId, options.providerOptions, provider.displayName, toast]);

  if (link) {
    return (
      <Flex gap="xs" align="center">
        <ArrowSquareOut size={15} className="shrink-0 text-muted-foreground" />
        <a
          href={link.externalUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-primary hover:underline"
        >
          {link.externalIdentifier}
        </a>
        <TagBadge
          name={provider.displayName}
          color={getTicketingProviderColor(provider.id)}
          hideDot
          size="sm"
        />
      </Flex>
    );
  }

  if (!canCreate) {
    return null;
  }

  if (isConfiguring) {
    return (
      <Flex gap="sm" align="center" wrap>
        {autoFileWarning}
        {options.optionFields}
        <Button
          variant="primary"
          size="sm"
          onPress={handleCreateTicket}
          isDisabled={createTicketMutation.isPending}
        >
          {createTicketMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onPress={() => setIsConfiguring(false)}
          isDisabled={createTicketMutation.isPending}
        >
          Cancel
        </Button>
      </Flex>
    );
  }

  return (
    <Flex gap="xs" align="center" wrap>
      {autoFileWarning}
      <Button
        variant="ghost"
        size="sm"
        onPress={() => {
          if (options.shouldConfirmBeforeCreate) {
            setIsConfiguring(true);
            return;
          }
          handleCreateTicket();
        }}
        isDisabled={createTicketMutation.isPending}
      >
        <ArrowSquareOut size={14} className="mr-1" />
        {createTicketMutation.isPending ? 'Creating...' : `Create ${provider.displayName} Ticket`}
      </Button>
    </Flex>
  );
}

export function IssueTicketingProviders({ issueId, providers }: IssueTicketingProvidersProps) {
  if (providers.length === 0) return null;

  return (
    <>
      {providers.map((provider) => (
        <IssueTicketingProviderRow key={provider.id} issueId={issueId} provider={provider} />
      ))}
    </>
  );
}
